import React from "react";
import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";
import "./App.css";
import ArmlResults from "./components/ArmlResults";
import Home from "./components/Home";
import LegacyRouteNotice from "./components/LegacyRouteNotice";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Recommendations from "./components/Recommendations";
import TermsOfService from "./components/TermsOfService";
import VentiHandoff from "./components/VentiHandoff";

const VENTIMATH = "https://ventimath.com";

const PRACTICE_ROUTES = [
  "/arml-warmup",
  "/practice-week/:weekId",
  "/practice-week/:weekId/set/:setId",
  "/performance/:practiceId",
  "/past-practices",
];

const HANDOFF_ROUTES = [
  {
    path: "/problems",
    eyebrow: "The problem library has moved",
    title: "Continue in the WARML contest archive",
    description:
      "Current WARML papers and problem collections now live in the shared contest archive.",
    destination: `${VENTIMATH}/apps/contests/warml`,
  },
  {
    path: "/scores",
    eyebrow: "Your submissions have moved",
    title: "Continue with your submitted work",
    description:
      "Your personal submissions and feedback now live with the rest of your mathematical work.",
    destination: `${VENTIMATH}/apps/my-submissions`,
  },
  {
    path: "/farml-attendance",
    eyebrow: "FARML information has moved",
    title: "Continue in the FARML contest hub",
    description:
      "Current FARML papers, information, and participation tools now live in the shared contest hub.",
    destination: `${VENTIMATH}/apps/contests/farml`,
  },
  {
    path: "/arml-attendance",
    eyebrow: "ARML trip information has moved",
    title: "Continue with the current ARML trip",
    description:
      "Current ARML registration and trip information now live together in the 2026 trip experience.",
    destination: `${VENTIMATH}/arml-2026`,
  },
  {
    path: "/arml-team-roles",
    eyebrow: "Team preferences have moved",
    title: "Continue with ARML team preferences",
    description:
      "Current team preferences and coach planning tools now live with the ARML contest experience.",
    destination: `${VENTIMATH}/apps/contests/arml-preferences`,
  },
  {
    path: "/roommate-assignments",
    eyebrow: "Room assignments have moved",
    title: "Continue in the ARML trip hub",
    description:
      "Current room assignments and traveler details now live together in the WARML trip hub.",
    destination: `${VENTIMATH}/apps/clubs/warml/trips/warml-2026`,
  },
  {
    path: "/plane-seats",
    eyebrow: "Flight details have moved",
    title: "Continue in the ARML trip hub",
    description:
      "Current flights, seat assignments, and traveler details now live together in the WARML trip hub.",
    destination: `${VENTIMATH}/apps/clubs/warml/trips/warml-2026`,
  },
  {
    path: "/chaperone-dashboard",
    eyebrow: "Chaperone tools have moved",
    title: "Continue with the ARML traveler grid",
    description:
      "Current chaperone and traveler information now lives in the ARML traveler grid.",
    destination: `${VENTIMATH}/apps/contests/arml-grid`,
  },
];

const PracticeHandoff = () => {
  const { practiceId } = useParams();
  const destination = practiceId
    ? `${VENTIMATH}/warml-practice/${encodeURIComponent(practiceId)}`
    : `${VENTIMATH}/warml-practice`;

  return (
    <VentiHandoff
      eyebrow="Practice has a new home"
      title="Keep working in Ventimath"
      description="WARML practice, problem sets, progress, and coach feedback now live in Ventimath. Your old bookmark still brought you to the right place."
      destination={destination}
    />
  );
};

export default function App() {
  return (
    <div className="App bg-[#f5f1e8] text-slate-950">
      <header className="site-header">
        <div className="site-shell flex min-h-20 items-center justify-between gap-6">
          <Link className="group flex items-center gap-3" to="/">
            <span className="brand-mark" aria-hidden="true">
              WA
            </span>
            <span>
              <span className="block text-xs font-bold uppercase tracking-[0.22em] text-emerald-300">
                Washington
              </span>
              <span className="block text-lg font-black tracking-tight text-white">
                ARML
              </span>
            </span>
          </Link>

          <nav
            aria-label="Primary navigation"
            className="hidden items-center gap-7 md:flex"
          >
            <Link className="site-nav-link" to="/arml-results">
              Results
            </Link>
            <Link className="site-nav-link" to="/recommendations">
              Resources
            </Link>
            <a className="site-nav-link" href="/#about">
              About
            </a>
          </nav>

          <a
            className="inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-extrabold text-[#4b2e83] transition hover:bg-violet-100"
            href="mailto:armlwacoach@gmail.com"
          >
            Contact
          </a>
        </div>
      </header>

      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/arml-results" element={<ArmlResults />} />
          <Route path="/recommendations" element={<Recommendations />} />
          <Route path="/practice/:practiceId" element={<PracticeHandoff />} />
          {PRACTICE_ROUTES.map((path) => (
            <Route key={path} path={path} element={<PracticeHandoff />} />
          ))}
          {HANDOFF_ROUTES.map(({ path, ...handoff }) => (
            <Route
              key={path}
              path={path}
              element={<VentiHandoff {...handoff} />}
            />
          ))}
          <Route
            path="/potd"
            element={
              <LegacyRouteNotice
                eyebrow="An older practice feature has retired"
                title="Problem of the Day is no longer published on this site."
                description="The old daily-problem feed has no direct successor. You can still explore current WARML resources and the team’s public results here."
              />
            }
          />
          <Route
            path="/potd/set/:setId"
            element={
              <LegacyRouteNotice
                eyebrow="An older practice feature has retired"
                title="This Problem of the Day set is no longer published."
                description="The archived set has no direct successor. You can still explore current WARML resources and the team’s public results here."
              />
            }
          />
          <Route
            path="/parent-communications"
            element={
              <LegacyRouteNotice
                eyebrow="An older coach tool has retired"
                title="The parent communication tracker is no longer maintained here."
                description="Current trip communication comes directly from the coaching team. Please contact the coaches if you need help with a student’s information."
                showContact
              />
            }
          />
          <Route
            path="/arml-week1-kickoff"
            element={
              <LegacyRouteNotice
                eyebrow="An older presentation has retired"
                title="The kickoff presentation is no longer part of the public archive."
                description="The presentation has no current replacement. The public site now keeps durable team results, program information, and resources instead."
              />
            }
          />
          <Route
            path="/token"
            element={
              <VentiHandoff
                eyebrow="Account tools have moved"
                title="Manage your account in Ventimath"
                description="Sign-in, account settings, and developer tools are now maintained in the personal Ventimath experience."
                destination={`${VENTIMATH}/apps/settings/developer`}
              />
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="border-t border-violet-900 bg-[#261442] text-violet-100">
        <div className="site-shell grid gap-8 py-10 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <p className="font-black text-white">Washington ARML</p>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">
              A Washington community preparing students for collaborative,
              high-level mathematics—and celebrating what they accomplish
              together.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <a href="mailto:armlwacoach@gmail.com">Contact</a>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms-of-service">Terms</Link>
          </div>
          <p className="text-xs text-slate-500 md:col-span-2">
            © {new Date().getFullYear()} Washington ARML
          </p>
        </div>
      </footer>
    </div>
  );
}
